//Built-in libraries
import { useState } from 'react';

//Third party libraries
import {
  CashIcon,
  ChartBarIcon,
  ClipboardListIcon,
  DocumentTextIcon,
  IdentificationIcon,
  PaperClipIcon,
} from '@heroicons/react/outline';
import { useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';

//Custom components
import { HookFormProvider } from 'components/UI/HookForm/Provider';
import { HookForm } from 'components/UI/HookForm/Form';
import Button from 'components/UI/Button';
import SubmitSuccess from 'components/Applications/SubmitSuccess';

//Services
import {
  ApplicationFormProvider,
  useApplicationFormContext,
} from 'features/ApplicationForm/ApplicationFormProvider';
import { postApplication } from 'api/Rover/ApplicationForm';

//STP Libraries

//Assets and Miscellaneous
import { initialValues } from './constants';
import { validationSchema } from './validationSchema';
import {
  IGetApplicationFormInfoServiceRes,
  IPostApplicationServicePayload,
} from 'api/Rover/ApplicationForm/types';
import Information from './Information';
import DefaultApplicantInformation from '../../Components/DefaultApplicantInformation';
import FinancialInformation from './FinancialInformation';
import ProjectOverview from './ProjectOverview';
import EconomicOverview from './EconomicOverview';
import Attachments from './Attachments';
import Disclosure from './Disclosure';

//Component Begin

const steps = [
  {
    name: 'Applicant Information',
    sectionId: 'applicantInformation',
    Icon: IdentificationIcon,
    component: <DefaultApplicantInformation />,
  },
  {
    name: 'Information',
    sectionId: 'step1',
    Icon: ClipboardListIcon,
    component: <Information />,
  },
  {
    name: 'Financial Information',
    sectionId: 'step2',
    Icon: ChartBarIcon,
    component: <FinancialInformation />,
  },
  {
    name: 'Project Overview',
    sectionId: 'step3',
    Icon: DocumentTextIcon,
    component: <ProjectOverview />,
  },
  {
    name: 'Economic Overview',
    sectionId: 'step4',
    Icon: CashIcon,
    component: <EconomicOverview />,
  },
  {
    name: 'Attachments',
    sectionId: 'step5',
    Icon: PaperClipIcon,
    component: <Attachments />,
  },
  {
    name: 'Disclosure',
    sectionId: 'step6',
    Icon: DocumentTextIcon,
    component: <Disclosure />,
  },
];

interface IBFIPApplicationFormProps {
  formData: IGetApplicationFormInfoServiceRes;
}

const ApplicationFormBase = ({ formData }: IBFIPApplicationFormProps) => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const { currentStep, setCurrentStep, updateData, data } =
    useApplicationFormContext();

  const isLast = currentStep === steps.length - 1;

  const submitApplication = useMutation(
    (data: IPostApplicationServicePayload) => postApplication({ data }),
    {
      onSuccess: () => {
        setIsSubmitSuccess(true);
      },
    }
  );

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      updateData({
        [steps[currentStep].sectionId]: values[steps[currentStep].sectionId],
      });
      setCurrentStep((prev) => prev + 1);
    } else {
      const applicationData: IPostApplicationServicePayload = {
        formId: formData?.data?.id,
        contactName: data.applicantInformation.fullName,
        contactEmail: data.applicantInformation.contactEmail,
        applicationData: { ...data, step6: { ...values.step6 } },
        files: [
          ...(data?.step5?.f1.selectedFiles || []),
          ...(data?.step5?.f2.selectedFiles|| []),
          ...(data?.step5?.f3.selectedFiles|| []),
          ...(data?.step5?.f4.selectedFiles|| []),
        ],
      };
      submitApplication.mutate(applicationData);
    }
  };

  if (isSubmitSuccess) {
    return (
      <SubmitSuccess
        message={
          formData.data.confirmationMessage ??
          'Thank you for your interest in BFIP. You will also receive a confirmation email with confirmation number.'
        }
      />
    );
  }

  return (
    <div className="container mx-auto">
      <h2>{formData?.data?.name}</h2>
      <div className="flex mb-10">
        <aside className="w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md">
          <div className="w-full flex justify-center">
            <img
              src={formData?.data.logo}
              alt="Form Logo"
              className="h-14 xl:h-20 object-scale-down "
            />
          </div>
          <nav className="space-y-1 py-5">
            {steps.map(({ name, Icon }, index) => (
              <button
                key={index}
                className={`${
                  currentStep === index
                    ? 'bg-neutralColor-lightest text-primaryColor-normal'
                    : 'text-neutralColor-normal'
                } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}>
                <Icon className="flex-shrink-0 -ml-1 mr-3 h-6 w-6" />
                {name}
              </button>
            ))}
          </nav>
        </aside>
        <div className="w-full bg-gray-50">
          <HookFormProvider
            defaultValues={initialValues}
            mode="all"
            resolver={yupResolver(validationSchema[currentStep])}>
            <HookForm onSubmit={onSubmitHandler}>
              {steps[currentStep].component}
              {isLast && (
                <div className="flex gap-4 justify-end p-6">
                  <Button
                    label="Previous"
                    btnStyle="neutral"
                    type="button"
                    disabled={submitApplication.isLoading}
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  />
                  <Button
                    label={`${
                      submitApplication.isLoading ? 'Submitting...' : 'Submit'
                    }`}
                    btnStyle="primary"
                    type="submit"
                    disabled={submitApplication.isLoading}
                  />
                </div>
              )}
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
    </div>
  );
};

export const BFIPApplicationForm = ({
  ...props
}: IBFIPApplicationFormProps) => {
  return (
    <ApplicationFormProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} />
    </ApplicationFormProvider>
  );
};
